/**
 * In-app reader for the vendored robotic 3D printing design guide.
 * Section HTML is sanitised at vendor time (scripts stripped, tag allowlist);
 * internal links are rewritten to /design-check/guide/* and intercepted so
 * navigation stays inside the SPA.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import DesignCheckLayout from './DesignCheckLayout';
import './DesignCheck.css';

function GuidePage() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const [sections, setSections] = useState([]);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const articleRef = useRef(null);

  useEffect(() => {
    fetch('/api/design-check/guide', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setSections(d.sections);
          if (!sectionId && d.sections.length) {
            navigate(`/design-check/guide/${d.sections[0].id}`, { replace: true });
          }
        }
      })
      .catch(() => {});
  }, [token, sectionId, navigate]);

  useEffect(() => {
    if (!sectionId) return;
    setLoading(true);
    fetch(`/api/design-check/guide/${sectionId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        setSection(d.success ? d.section : null);
        setLoading(false);
        window.scrollTo({ top: 0 });
      })
      .catch(() => setLoading(false));
  }, [sectionId, token]);

  // Keep in-guide links inside the SPA
  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    const onClick = (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('/design-check/')) {
        e.preventDefault();
        navigate(href);
      } else if (href.startsWith('http')) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [navigate, section]);

  return (
    <DesignCheckLayout>
      <div className="dc-guide">
        <nav className="dc-guide-nav">
          {sections.map((s) => (
            <NavLink key={s.id} to={`/design-check/guide/${s.id}`}>
              <span className="dc-guide-num">{s.num}</span>
              {s.title.replace(/^\d+:\s*/, '')}
            </NavLink>
          ))}
        </nav>
        <div className="dc-guide-article" ref={articleRef}>
          {loading && <div className="dc-spinner" style={{ marginTop: 60 }} />}
          {!loading && section && section.html && (
            <>
              <div dangerouslySetInnerHTML={{ __html: section.html }} />
              <div className="dc-guide-attribution">
                {section.sourceUrl ? (
                  <>
                    Source:{' '}
                    <a href={section.sourceUrl} target="_blank" rel="noopener noreferrer">
                      {section.sourceLabel}
                    </a>
                  </>
                ) : (
                  <>
                    Rapid Fusion design guide for robotic 3D printing. Diagrams adapted
                    from the "More Than Layers" design guide.
                  </>
                )}
              </div>
            </>
          )}
          {!loading && section && !section.html && (
            <div className="dc-center-page">
              <h1>{section.title}</h1>
              <p>{section.summary}</p>
              <a className="dc-btn primary" href={section.externalUrl} target="_blank" rel="noopener noreferrer">
                Read this chapter online →
              </a>
            </div>
          )}
        </div>
      </div>
    </DesignCheckLayout>
  );
}

export default GuidePage;
